import { useEffect, useRef, useState } from "react";
import { Check, Search, X } from "react-feather";
import { countries, Country } from "../../constants/countries";
import { Input } from "../ui/input";

export const AGE_MIN = 18;
// Slider cap: 65 on the upper thumb means "65 and older" (no maxAge sent)
export const AGE_MAX = 65;

export interface AgeFilter {
  range: [number, number] | null; // null = any age
  includeNoAge: boolean;
}

export interface RevenueFilter {
  min: number | null;
  max: number | null;
}

interface FilterOption {
  label: string;
  value: string;
}

interface AffiliateFilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  nicheOptions: FilterOption[];
  niche: string[];
  onNicheChange: (value: string[]) => void;
  gender: string;
  onGenderChange: (value: "MALE" | "FEMALE" | "") => void;
  country: string[];
  onCountryChange: (value: string[]) => void;
  age: AgeFilter;
  onAgeChange: (value: AgeFilter) => void;
  revenue: RevenueFilter;
  onRevenueChange: (value: RevenueFilter) => void;
  onResetAll: () => void;
}

const formatMoney = (n: number) => `$${n.toLocaleString("en-US")}`;
const formatAge = (n: number) => (n >= AGE_MAX ? `${AGE_MAX}+` : `${n}`);

const REVENUE_PRESETS: Array<{
  label: string;
  min: number | null;
  max: number | null;
}> = [
  { label: "Any", min: null, max: null },
  { label: "$1+", min: 1, max: null },
  { label: "$100+", min: 100, max: null },
  { label: "$1k+", min: 1000, max: null },
  { label: "$5k+", min: 5000, max: null },
];

/* ------------------------------------------------------------------ */
/* Dual-thumb range slider: pointer-based, keyboard accessible, and    */
/* only commits when the drag ends so a stray pixel never applies.     */
/* ------------------------------------------------------------------ */

interface DualRangeProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  onCommit: (value: [number, number]) => void;
  format: (value: number) => string;
}

function DualRange({
  min,
  max,
  value,
  onChange,
  onCommit,
  format,
}: DualRangeProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<number | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const pct = (v: number) => ((v - min) / (max - min)) * 100;

  const valueFromPointer = (clientX: number) => {
    const rect = trackRef.current!.getBoundingClientRect();
    const p = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return Math.round(min + p * (max - min));
  };

  const moveThumb = (clientX: number) => {
    const index = activeRef.current;
    if (index === null) return;
    const v = valueFromPointer(clientX);
    const current = valueRef.current;
    const next: [number, number] =
      index === 0
        ? [Math.min(v, current[1]), current[1]]
        : [current[0], Math.max(v, current[0])];
    if (next[0] !== current[0] || next[1] !== current[1]) onChange(next);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const p = (e.clientX - rect.left) / rect.width;
    const current = valueRef.current;
    const d0 = Math.abs(p * 100 - pct(current[0]));
    const d1 = Math.abs(p * 100 - pct(current[1]));
    activeRef.current = d0 < d1 ? 0 : d1 < d0 ? 1 : p * 100 < pct(current[0]) ? 0 : 1;
    e.currentTarget.setPointerCapture(e.pointerId);
    moveThumb(e.clientX);
    e.preventDefault();
  };

  const endDrag = () => {
    if (activeRef.current === null) return;
    activeRef.current = null;
    onCommit(valueRef.current);
  };

  const handleThumbKeyDown = (index: number) => (
    e: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    const step = e.shiftKey ? 5 : 1;
    const current = valueRef.current;
    let v = current[index];
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") v -= step;
    else if (e.key === "ArrowRight" || e.key === "ArrowUp") v += step;
    else if (e.key === "Home") v = min;
    else if (e.key === "End") v = max;
    else return;
    e.preventDefault();
    v = Math.min(max, Math.max(min, v));
    const next: [number, number] =
      index === 0
        ? [Math.min(v, current[1]), current[1]]
        : [current[0], Math.max(v, current[0])];
    if (next[0] !== current[0] || next[1] !== current[1]) {
      onChange(next);
      onCommit(next);
    }
  };

  return (
    <div
      ref={trackRef}
      className="relative h-7 cursor-pointer touch-none select-none"
      onPointerDown={handlePointerDown}
      onPointerMove={(e) => moveThumb(e.clientX)}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div className="absolute left-0 right-0 top-3 h-1 rounded-full bg-brand-100" />
      <div
        className="absolute top-3 h-1 rounded-full bg-brand-600"
        style={{
          left: `${pct(value[0])}%`,
          width: `${pct(value[1]) - pct(value[0])}%`,
        }}
      />
      {[0, 1].map((index) => (
        <button
          key={index}
          type="button"
          role="slider"
          aria-label={index === 0 ? "Minimum" : "Maximum"}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value[index]}
          aria-valuetext={format(value[index])}
          onKeyDown={handleThumbKeyDown(index)}
          className="absolute top-1 h-5 w-5 -ml-2.5 rounded-full border-2 border-brand-600 bg-card shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1"
          style={{ left: `${pct(value[index])}%` }}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pill + popover shell                                                */
/* ------------------------------------------------------------------ */

interface FilterPillProps {
  label: string;
  valueLabel: string | null; // null = inactive
  open: boolean;
  onToggle: () => void;
  onClear: () => void;
  popoverAlign?: "left" | "right";
  popoverClassName?: string;
  children: React.ReactNode;
  anchorRef: (el: HTMLDivElement | null) => void;
}

function FilterPill({
  label,
  valueLabel,
  open,
  onToggle,
  onClear,
  popoverAlign = "left",
  popoverClassName = "",
  children,
  anchorRef,
}: FilterPillProps) {
  const active = valueLabel !== null;
  return (
    <div className="relative" ref={anchorRef}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 py-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
          active
            ? "border-brand-200 bg-brand-50 font-medium text-brand-700"
            : "border-border bg-card text-muted-foreground hover:border-border"
        }`}
      >
        {active ? valueLabel : label}
        {active ? (
          <span
            role="button"
            aria-label={`Clear ${label} filter`}
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            className="flex h-4 w-4 items-center justify-center rounded-full text-brand-700 hover:bg-brand-100"
          >
            <X size={12} />
          </span>
        ) : (
          <svg
            className={`h-4 w-4 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </button>
      {open && (
        <div
          className={`absolute z-20 mt-2 rounded-xl border border-border bg-card p-4 shadow-lg ${
            popoverAlign === "right" ? "right-0" : "left-0"
          } ${popoverClassName}`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function PopoverTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

function CheckOption({
  checked,
  label,
  onClick,
}: {
  checked: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <div
      className={`flex cursor-pointer items-center rounded-md px-2 py-2 hover:bg-accent ${
        checked ? "bg-brand-50" : ""
      }`}
      onClick={onClick}
    >
      <div className="mr-2 flex h-5 w-5 items-center justify-center rounded-md border border-border">
        {checked && <Check className="h-4 w-4 text-brand-600" />}
      </div>
      <span className="text-sm">{label}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The filter bar                                                      */
/* ------------------------------------------------------------------ */

export function AffiliateFilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search name or email",
  nicheOptions,
  niche,
  onNicheChange,
  gender,
  onGenderChange,
  country,
  onCountryChange,
  age,
  onAgeChange,
  revenue,
  onRevenueChange,
  onResetAll,
}: AffiliateFilterBarProps) {
  const [openPill, setOpenPill] = useState<string | null>(null);
  const [countrySearch, setCountrySearch] = useState("");
  const anchorRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Age slider draft: edited while dragging, committed to the parent on release
  const [ageDraft, setAgeDraft] = useState<[number, number]>(
    age.range ?? [AGE_MIN, AGE_MAX]
  );
  const [ageMinText, setAgeMinText] = useState("");
  const [ageMaxText, setAgeMaxText] = useState("");
  useEffect(() => {
    setAgeDraft(age.range ?? [AGE_MIN, AGE_MAX]);
    setAgeMinText(age.range ? String(age.range[0]) : "");
    setAgeMaxText(age.range ? formatAge(age.range[1]) : "");
  }, [age.range]);

  // Revenue inputs: free text until blur/Enter
  const [revMinText, setRevMinText] = useState("");
  const [revMaxText, setRevMaxText] = useState("");
  useEffect(() => {
    setRevMinText(revenue.min == null ? "" : String(revenue.min));
    setRevMaxText(revenue.max == null ? "" : String(revenue.max));
  }, [revenue.min, revenue.max]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!openPill) return;
      const anchor = anchorRefs.current[openPill];
      if (anchor && !anchor.contains(event.target as Node)) {
        setOpenPill(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openPill]);

  const togglePill = (key: string) => {
    setOpenPill((prev) => (prev === key ? null : key));
    if (key === "country") setCountrySearch("");
  };

  const commitAge = (range: [number, number]) => {
    const isFullRange = range[0] === AGE_MIN && range[1] === AGE_MAX;
    onAgeChange({ ...age, range: isFullRange ? null : range });
  };

  const commitAgeInputs = (minText: string, maxText: string) => {
    const parse = (text: string, fallback: number) => {
      const parsed = parseInt(text.replace(/\D/g, ""), 10);
      const v = Number.isNaN(parsed) ? fallback : parsed;
      return Math.min(AGE_MAX, Math.max(AGE_MIN, v));
    };
    let lo = parse(minText, AGE_MIN);
    let hi = parse(maxText, AGE_MAX);
    if (hi < lo) [lo, hi] = [hi, lo];
    const next: [number, number] = [lo, hi];
    setAgeDraft(next);
    commitAge(next);
  };

  const commitRevenueInputs = (minText: string, maxText: string) => {
    const parse = (text: string) => {
      const parsed = parseInt(text.replace(/[^0-9]/g, ""), 10);
      return Number.isNaN(parsed) ? null : parsed;
    };
    let min = parse(minText);
    let max = parse(maxText);
    if (min != null && max != null && max < min) [min, max] = [max, min];
    onRevenueChange({ min, max });
  };

  /* Pill value labels (null = inactive) */
  const nicheLabel =
    niche.length === 0
      ? null
      : niche.length === 1
      ? nicheOptions.find((o) => o.value === niche[0])?.label ?? "Niche · 1"
      : `Niche · ${niche.length}`;

  const genderLabel = gender
    ? gender.charAt(0) + gender.slice(1).toLowerCase()
    : null;

  const countryLabel =
    country.length === 0
      ? null
      : country.length === 1
      ? countries.find((c: Country) => c.code === country[0])?.name ??
        "Country · 1"
      : `Country · ${country.length}`;

  const ageLabel = age.range
    ? `Age ${age.range[0]}–${formatAge(age.range[1])}`
    : null;

  const revenueLabel =
    revenue.min == null && revenue.max == null
      ? null
      : revenue.max == null
      ? `${formatMoney(revenue.min ?? 0)}+`
      : `${formatMoney(revenue.min ?? 0)}–${formatMoney(revenue.max)}`;

  const anyActive =
    niche.length > 0 ||
    !!gender ||
    country.length > 0 ||
    age.range !== null ||
    revenue.min != null ||
    revenue.max != null ||
    searchValue !== "";

  const filteredCountries = countries.filter((c: Country) =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Search */}
      <div className="relative w-full sm:w-auto sm:min-w-[220px] sm:flex-1 sm:max-w-xs">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Search className="h-4 w-4 text-muted-foreground" />
        </div>
        <Input
          type="text"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Niche */}
      <FilterPill
        label="Niche"
        valueLabel={nicheLabel}
        open={openPill === "niche"}
        onToggle={() => togglePill("niche")}
        onClear={() => onNicheChange([])}
        popoverClassName="w-56"
        anchorRef={(el) => (anchorRefs.current["niche"] = el)}
      >
        <PopoverTitle>Niche</PopoverTitle>
        <div className="max-h-60 overflow-y-auto">
          {nicheOptions.map((option) => (
            <CheckOption
              key={option.value}
              checked={niche.includes(option.value)}
              label={option.label}
              onClick={() =>
                onNicheChange(
                  niche.includes(option.value)
                    ? niche.filter((v) => v !== option.value)
                    : [...niche, option.value]
                )
              }
            />
          ))}
        </div>
      </FilterPill>

      {/* Gender */}
      <FilterPill
        label="Gender"
        valueLabel={genderLabel}
        open={openPill === "gender"}
        onToggle={() => togglePill("gender")}
        onClear={() => onGenderChange("")}
        popoverClassName="w-44"
        anchorRef={(el) => (anchorRefs.current["gender"] = el)}
      >
        <PopoverTitle>Gender</PopoverTitle>
        {[
          { label: "Any", value: "" },
          { label: "Female", value: "FEMALE" },
          { label: "Male", value: "MALE" },
        ].map((option) => (
          <CheckOption
            key={option.value}
            checked={gender === option.value}
            label={option.label}
            onClick={() => {
              onGenderChange(option.value as "MALE" | "FEMALE" | "");
              setOpenPill(null);
            }}
          />
        ))}
      </FilterPill>

      {/* Country */}
      <FilterPill
        label="Country"
        valueLabel={countryLabel}
        open={openPill === "country"}
        onToggle={() => togglePill("country")}
        onClear={() => onCountryChange([])}
        popoverClassName="w-64"
        anchorRef={(el) => (anchorRefs.current["country"] = el)}
      >
        <PopoverTitle>Country</PopoverTitle>
        <div className="relative mb-2">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <input
            type="text"
            placeholder="Search"
            value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
            className="w-full rounded-lg border border-border py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div className="max-h-60 overflow-y-auto">
          {filteredCountries.map((c: Country) => (
            <CheckOption
              key={c.code}
              checked={country.includes(c.code)}
              label={c.name}
              onClick={() =>
                onCountryChange(
                  country.includes(c.code)
                    ? country.filter((v) => v !== c.code)
                    : [...country, c.code]
                )
              }
            />
          ))}
        </div>
      </FilterPill>

      {/* Age */}
      <FilterPill
        label="Age"
        valueLabel={ageLabel}
        open={openPill === "age"}
        onToggle={() => togglePill("age")}
        onClear={() => onAgeChange({ ...age, range: null })}
        popoverClassName="w-72"
        anchorRef={(el) => (anchorRefs.current["age"] = el)}
      >
        <PopoverTitle>Age range</PopoverTitle>
        <div className="flex items-center gap-2">
          <input
            type="text"
            inputMode="numeric"
            aria-label="Minimum age"
            placeholder={`${AGE_MIN}`}
            value={ageMinText}
            onChange={(e) => setAgeMinText(e.target.value)}
            onBlur={() => commitAgeInputs(ageMinText, ageMaxText)}
            onKeyDown={(e) =>
              e.key === "Enter" && commitAgeInputs(ageMinText, ageMaxText)
            }
            className="w-full rounded-lg border border-border bg-muted px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <span className="text-muted-foreground">–</span>
          <input
            type="text"
            inputMode="numeric"
            aria-label="Maximum age"
            placeholder={`${AGE_MAX}+`}
            value={ageMaxText}
            onChange={(e) => setAgeMaxText(e.target.value)}
            onBlur={() => commitAgeInputs(ageMinText, ageMaxText)}
            onKeyDown={(e) =>
              e.key === "Enter" && commitAgeInputs(ageMinText, ageMaxText)
            }
            className="w-full rounded-lg border border-border bg-muted px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div className="mt-3">
          <DualRange
            min={AGE_MIN}
            max={AGE_MAX}
            value={ageDraft}
            onChange={(next) => {
              setAgeDraft(next);
              setAgeMinText(String(next[0]));
              setAgeMaxText(formatAge(next[1]));
            }}
            onCommit={commitAge}
            format={formatAge}
          />
          <div className="flex justify-between text-xs tabular-nums text-muted-foreground">
            <span>{formatAge(ageDraft[0])}</span>
            <span>{formatAge(ageDraft[1])}</span>
          </div>
        </div>
        <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={age.includeNoAge}
            onChange={(e) =>
              onAgeChange({ ...age, includeNoAge: e.target.checked })
            }
            className="h-4 w-4 rounded border-border accent-brand-600"
          />
          Include profiles without an age
        </label>
      </FilterPill>

      {/* Revenue */}
      <FilterPill
        label="Revenue"
        valueLabel={revenueLabel}
        open={openPill === "revenue"}
        onToggle={() => togglePill("revenue")}
        onClear={() => onRevenueChange({ min: null, max: null })}
        popoverAlign="right"
        popoverClassName="w-72"
        anchorRef={(el) => (anchorRefs.current["revenue"] = el)}
      >
        <PopoverTitle>Revenue generated</PopoverTitle>
        <div className="flex items-center gap-2">
          <div className="flex flex-1 items-center gap-1 rounded-lg border border-border bg-muted px-3 py-1.5">
            <span className="text-sm text-muted-foreground">$</span>
            <input
              type="text"
              inputMode="numeric"
              aria-label="Minimum revenue"
              placeholder="0"
              value={revMinText}
              onChange={(e) => setRevMinText(e.target.value)}
              onBlur={() => commitRevenueInputs(revMinText, revMaxText)}
              onKeyDown={(e) =>
                e.key === "Enter" && commitRevenueInputs(revMinText, revMaxText)
              }
              className="w-full bg-transparent text-sm tabular-nums focus:outline-none"
            />
          </div>
          <span className="text-muted-foreground">–</span>
          <div className="flex flex-1 items-center gap-1 rounded-lg border border-border bg-muted px-3 py-1.5">
            <span className="text-sm text-muted-foreground">$</span>
            <input
              type="text"
              inputMode="numeric"
              aria-label="Maximum revenue"
              placeholder="Any"
              value={revMaxText}
              onChange={(e) => setRevMaxText(e.target.value)}
              onBlur={() => commitRevenueInputs(revMinText, revMaxText)}
              onKeyDown={(e) =>
                e.key === "Enter" && commitRevenueInputs(revMinText, revMaxText)
              }
              className="w-full bg-transparent text-sm tabular-nums focus:outline-none"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {REVENUE_PRESETS.map((preset) => {
            const on =
              revenue.min === preset.min && revenue.max === preset.max;
            return (
              <button
                key={preset.label}
                type="button"
                onClick={() =>
                  onRevenueChange({ min: preset.min, max: preset.max })
                }
                className={`rounded-lg border px-3 py-1 text-xs transition-colors ${
                  on
                    ? "border-brand-200 bg-brand-50 font-medium text-brand-700"
                    : "border-border bg-card text-muted-foreground hover:border-border hover:text-muted-foreground"
                }`}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      </FilterPill>

      {/* Reset */}
      {anyActive && (
        <button
          type="button"
          onClick={onResetAll}
          className="px-2 py-1.5 text-sm text-muted-foreground underline underline-offset-4 hover:text-muted-foreground"
        >
          Reset all
        </button>
      )}
    </div>
  );
}
